import { useEffect, useState } from "react";

/**
 * True while `query` matches the viewport. The shell uses this for the locked rail collapse
 * order (861-1439px yields the app rail to icons), so the value has to track live resizes.
 *
 * Environments without a real matchMedia (jsdom reports `matches: false` for everything and
 * older stubs may miss addEventListener) resolve to the query not matching, which is the
 * wide-desktop default: tests and headless renders get the expanded rail unless they stub
 * the media list themselves.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const list = window.matchMedia(query);
    setMatches(list.matches);
    if (typeof list.addEventListener !== "function") {
      return;
    }
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
